import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { usePortalActiveClinic } from '@/lib/portalActiveClinic';
import { logoUrlFromBranding } from '@/lib/clinicBranding';
import type { Database, Json } from '@/types/database.generated';

export type AppointmentStatus = Database['public']['Enums']['appointment_status'];

export interface PortalAppointmentItem {
  id: string;
  clinic_id: string;
  clinic_name: string;
  clinic_logo: string | null;
  patient_id: string;
  clinician_id: string | null;
  clinician_name: string | null;
  service_id: string | null;
  service_name: string | null;
  service_color: string | null;
  starts_at: string;
  ends_at: string;
  status: AppointmentStatus;
  notes: string | null;
  cancellation_allowed: boolean;
}

export const portalAppointmentsQueryKey = (patientId: string | null) =>
  ['portal', 'appointments', patientId] as const;

/**
 * Hook to fetch appointments for the active portal clinic.
 */
export function usePortalAppointments() {
  const { activePatientId, isLoading: clinicsLoading } = usePortalActiveClinic();

  const query = useQuery({
    queryKey: portalAppointmentsQueryKey(activePatientId),
    enabled: Boolean(activePatientId),
    queryFn: async (): Promise<PortalAppointmentItem[]> => {
      if (!activePatientId) return [];

      const { data, error } = await supabase
        .from('appointments')
        .select(
          'id, clinic_id, patient_id, clinician_id, service_id, service_name, starts_at, ends_at, status, notes, ' +
          'clinic:clinics!appointments_clinic_id_fkey(name,branding), ' +
          'clinician:users!appointments_clinician_id_fkey(name,email), ' +
          'service:services!appointments_service_id_fkey(color)'
        )
        .eq('patient_id', activePatientId)
        .order('starts_at', { ascending: false });

      if (error) throw error;

      type Joined = {
        id: string;
        clinic_id: string;
        patient_id: string;
        clinician_id: string | null;
        service_id: string | null;
        service_name: string | null;
        starts_at: string;
        ends_at: string;
        status: AppointmentStatus;
        notes: string | null;
        clinic: { name: string; branding: Json | null } | null;
        clinician: { name: string | null; email: string } | null;
        service: { color: string | null } | null;
      };

      return ((data ?? []) as unknown as Joined[]).map(
        (r): PortalAppointmentItem => {
          const startsAt = new Date(r.starts_at);
          const now = new Date();
          const hoursUntilAppointment = (startsAt.getTime() - now.getTime()) / (1000 * 60 * 60);
          const cancellationAllowed =
            r.status === 'upcoming' && hoursUntilAppointment >= 24;

          return {
            id: r.id,
            clinic_id: r.clinic_id,
            clinic_name: r.clinic?.name ?? '—',
            clinic_logo: logoUrlFromBranding(r.clinic?.branding),
            patient_id: r.patient_id,
            clinician_id: r.clinician_id,
            clinician_name: r.clinician?.name ?? r.clinician?.email ?? null,
            service_id: r.service_id,
            service_name: r.service_name,
            service_color: r.service?.color ?? null,
            starts_at: r.starts_at,
            ends_at: r.ends_at,
            status: r.status,
            notes: r.notes,
            cancellation_allowed: cancellationAllowed,
          };
        }
      );
    },
    staleTime: 15_000,
  });

  return {
    ...query,
    isLoading: clinicsLoading || query.isLoading,
  };
}

export function usePortalCancelAppointment() {
  const qc = useQueryClient();
  const { activePatientId } = usePortalActiveClinic();

  return useMutation({
    mutationFn: async (input: {
      appointment_id: string;
      reason?: string | null;
    }) => {
      const { data, error } = await supabase.rpc('portal_cancel_appointment', {
        p_appointment_id: input.appointment_id,
        p_reason: input.reason ?? 'Cancelled by patient via portal',
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: portalAppointmentsQueryKey(activePatientId),
      });
    },
  });
}
